import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
  Settings, 
  Wallet, 
  Shield, 
  CheckCircle, 
  AlertCircle, 
  Eye, 
  EyeOff,
  Loader,
  ExternalLink
} from 'lucide-react';
import API_ENDPOINTS from '../../config/api';

const AdminNOWPayments = () => {
  const [config, setConfig] = useState({
    configured: false,
    api_key: '',
    ipn_secret: '',
    admin_wallet_address: '',
    sandbox_mode: false,
    admin_fee_interval: 5
  });
  
  const [formData, setFormData] = useState({
    api_key: '',
    ipn_secret: '',
    admin_wallet_address: '',
    sandbox_mode: false,
    admin_fee_interval: 5
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState({
    api_key: false,
    ipn_secret: false
  });
  const [apiStatus, setApiStatus] = useState(null);

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.ADMIN}/nowpayments/config`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        
        if (data.configured) {
          setFormData({
            api_key: '',
            ipn_secret: '',
            admin_wallet_address: data.admin_wallet_address || '',
            sandbox_mode: data.sandbox_mode || false,
            admin_fee_interval: data.admin_fee_interval || 5
          });
        }
      } else {
        toast.error('Failed to load NOWPayments configuration');
      }
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('Error loading configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.api_key || !formData.ipn_secret || !formData.admin_wallet_address) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate Tron wallet address
    if (!formData.admin_wallet_address.startsWith('T') || formData.admin_wallet_address.length !== 34) {
      toast.error('Invalid Tron wallet address format');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.ADMIN}/nowpayments/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('NOWPayments configuration saved successfully');
        await loadConfiguration();
        
        // Clear sensitive fields after saving
        setFormData(prev => ({
          ...prev,
          api_key: '',
          ipn_secret: ''
        }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.ADMIN}/nowpayments/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApiStatus(data);
        
        if (data.status === 'connected') {
          toast.success('NOWPayments API connection successful');
        } else {
          toast.error(data.message || 'Connection failed');
        }
      } else {
        toast.error('Failed to test connection');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Error testing connection');
    } finally {
      setTesting(false);
    }
  };

  const toggleShowSecret = (field) => {
    setShowSecrets(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">NOWPayments Configuration</h1>
          <p className="text-gray-600 mt-2">
            Configure cryptocurrency payment processing for member-to-member transactions
          </p>
        </div>
        
        {config.configured && (
          <Button onClick={testConnection} disabled={testing} variant="outline">
            {testing ? <Loader className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
            Test Connection
          </Button>
        )}
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className={`p-3 rounded-full ${config.configured ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Settings className={`w-6 h-6 ${config.configured ? 'text-green-600' : 'text-gray-600'}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Configuration</p>
                <p className="text-2xl font-bold text-gray-900">
                  {config.configured ? 'Configured' : 'Not Configured'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className={`p-3 rounded-full ${apiStatus?.status === 'connected' ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Shield className={`w-6 h-6 ${apiStatus?.status === 'connected' ? 'text-green-600' : 'text-gray-600'}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">API Status</p>
                <p className="text-2xl font-bold text-gray-900">
                  {apiStatus?.status === 'connected' ? 'Connected' : 'Not Connected'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className={`p-3 rounded-full ${config.sandbox_mode ? 'bg-yellow-100' : 'bg-blue-100'}`}>
                <Wallet className={`w-6 h-6 ${config.sandbox_mode ? 'text-yellow-600' : 'text-blue-600'}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Environment</p>
                <p className="text-2xl font-bold text-gray-900">
                  {config.sandbox_mode ? 'Sandbox' : 'Production'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configuration Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Settings className="w-5 h-5 mr-2" />
            NOWPayments Settings
          </CardTitle>
          <CardDescription>
            Configure your NOWPayments API credentials and payment settings. 
            All sensitive data is encrypted before storage.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key *
            </label>
            <div className="relative">
              <input
                type={showSecrets.api_key ? 'text' : 'password'}
                value={formData.api_key}
                onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                placeholder={config.configured ? "Enter new API key to update" : "Enter your NOWPayments API key"}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => toggleShowSecret('api_key')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showSecrets.api_key ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
            {config.configured && (
              <p className="text-sm text-gray-500 mt-1">
                Current: {config.api_key || 'Not set'}
              </p>
            )}
          </div>

          {/* IPN Secret */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              IPN Secret *
            </label>
            <div className="relative">
              <input
                type={showSecrets.ipn_secret ? 'text' : 'password'}
                value={formData.ipn_secret}
                onChange={(e) => setFormData(prev => ({ ...prev, ipn_secret: e.target.value }))}
                placeholder={config.configured ? "Enter new IPN secret to update" : "Enter your IPN secret"}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => toggleShowSecret('ipn_secret')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showSecrets.ipn_secret ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
            {config.configured && (
              <p className="text-sm text-gray-500 mt-1">
                Current: {config.ipn_secret || 'Not set'}
              </p>
            )}
          </div>

          {/* Admin Wallet Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admin Wallet Address *
            </label>
            <input
              type="text"
              value={formData.admin_wallet_address}
              onChange={(e) => setFormData(prev => ({ ...prev, admin_wallet_address: e.target.value }))}
              placeholder="TRon wallet address (starts with T, 34 characters)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              Every 5th payment will be sent to this wallet address
            </p>
          </div>

          {/* Sandbox Mode */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.sandbox_mode}
                onChange={(e) => setFormData(prev => ({ ...prev, sandbox_mode: e.target.checked }))}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Use Sandbox Mode</span>
            </label>
            <p className="text-sm text-gray-500 mt-1">
              Enable for testing. Disable for production use.
            </p>
          </div>

          {/* Admin Fee Interval */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admin Fee Interval
            </label>
            <select
              value={formData.admin_fee_interval}
              onChange={(e) => setFormData(prev => ({ ...prev, admin_fee_interval: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={3}>Every 3rd payment</option>
              <option value={5}>Every 5th payment</option>
              <option value={10}>Every 10th payment</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              How often payments should go to the admin wallet instead of matrix members
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save Configuration
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            Setup Instructions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Getting Your NOWPayments Credentials:</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
              <li>Visit <a href="https://nowpayments.io" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center">
                NOWPayments.io <ExternalLink className="w-3 h-3 ml-1" />
              </a> and create an account</li>
              <li>Complete the verification process</li>
              <li>Go to Settings → API Keys to generate your API key</li>
              <li>Go to Settings → IPN to set up your IPN secret</li>
              <li>Configure your webhook URL: <code className="bg-white px-2 py-1 rounded text-xs">
                {window.location.origin}/api/webhooks/nowpayments
              </code></li>
            </ol>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-medium text-yellow-900 mb-2">Important Security Notes:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
              <li>API keys and IPN secrets are encrypted before storage</li>
              <li>Use sandbox mode for testing before going live</li>
              <li>Never share your API credentials</li>
              <li>Monitor payment logs regularly</li>
            </ul>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-medium text-green-900 mb-2">Matrix Payment Flow:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
              <li>Members pay directly to other member wallets (non-custodial)</li>
              <li>System automatically routes payments based on matrix positions</li>
              <li>Auto-repurchase creates new positions automatically</li>
              <li>Every {config.admin_fee_interval || 5}th payment goes to admin wallet</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNOWPayments;